/* Courted · Swipe: places near home, one card at a time, on your own. Drag right (or ♥) for yes, left (or ✕) for
   no; Not sure yet brings it back later, Been there asks how it was (Loved it, or Not again). Your partner's yeses
   and favourites come first, with "Olya said yes" or "Olya loved it" on the card. A card is Google's photo of the place (only the top card's is fetched), what it is,
   the street, the drive, and our line and tags when the catalogue has the place; on the top card, Google's rating and
   its one line about the place (DOM-26). Over the deck, one line says what, where and when ("Dinner in Toronto"),
   and opens What and where. */
import React from 'react';
import * as H from './helpers.js';
import * as S from './sync.js';
import { Ico, Plate, Google, track } from './ui.jsx';
import { guessedYes } from '../core/guesses.js';

const { useState, useRef, useEffect, useCallback } = React;
const THRESHOLD = 110, DEAD = 12;

function Card({ item, card, cat, top, partnerName, words, onDecide, onOpen }) {
  // Would Olya? (0061): your partner guessed you'll say yes
  const [guessed, setGuessed] = React.useState(() => new Set());
  React.useEffect(() => { if (top && item.partnerYes) guessedYes().then(setGuessed); }, [top, item.id]);
  const [drag, setDrag] = useState({ x: 0, y: 0, active: false });
  const [leaving, setLeaving] = useState(null);
  const start = useRef(null);
  const commit = useCallback((v) => {
    if (leaving) return;
    setLeaving(v);
    setTimeout(() => onDecide(v), 230);
  }, [leaving, onDecide]);
  useEffect(() => {
    if (!top) return undefined;
    const onKey = (e) => {
      if (e.target && /INPUT|TEXTAREA|SELECT/.test(e.target.tagName)) return;
      if (document.querySelector('.co-page, .co-sheet-wrap')) return;
      if (e.key === 'ArrowRight') commit('yes');
      if (e.key === 'ArrowLeft') commit('no');
      if (e.key === 'ArrowDown') commit('later');
      if (e.key === 'ArrowUp') commit('been');
    };
    window.addEventListener('keydown', onKey);
    window.__coCommit = commit;
    return () => { window.removeEventListener('keydown', onKey); if (window.__coCommit === commit) window.__coCommit = null; };
  }, [top, commit]);
  const onDown = (e) => {
    if (!top || leaving || (e.pointerType === 'mouse' && e.button !== 0)) return;
    start.current = { x: e.clientX, y: e.clientY, t: Date.now(), horizontal: null };
  };
  const onMove = (e) => {
    const st = start.current; if (!st) return;
    const dx = e.clientX - st.x, dy = e.clientY - st.y;
    if (st.horizontal === null) {
      if (Math.abs(dx) < DEAD && Math.abs(dy) < DEAD) return;
      st.horizontal = Math.abs(dx) > Math.abs(dy) * 1.3;
      if (st.horizontal) { try { e.currentTarget.setPointerCapture(e.pointerId); } catch (err) {} } else { start.current = null; return; }
    }
    setDrag({ x: dx, y: dy * 0.25, active: true });
  };
  const onUp = (e) => {
    const st = start.current; if (!st) return;
    start.current = null;
    const dx = e.clientX - st.x, dy = e.clientY - st.y, dt = Date.now() - st.t;
    if (st.horizontal === null) {
      if (Math.abs(dx) < DEAD && Math.abs(dy) < DEAD && dt < 350) onOpen();
      setDrag({ x: 0, y: 0, active: false });
      return;
    }
    const flick = Math.abs(dx) > 60 && dt < 220;
    if (dx > THRESHOLD || (flick && dx > 0)) commit('yes');
    else if (dx < -THRESHOLD || (flick && dx < 0)) commit('no');
    else setDrag({ x: 0, y: 0, active: false });
  };
  const x = leaving ? (leaving === 'yes' ? 520 : leaving === 'no' ? -520 : 0) : drag.x;
  const y = leaving === 'been' ? -560 : leaving === 'later' ? 560 : leaving ? -30 : drag.y;
  const style = top ? { transform: 'translate(' + x + 'px,' + y + 'px) rotate(' + (x / 26) + 'deg)', opacity: leaving ? 0 : 1 } : undefined;
  const yes = Math.max(0, Math.min(1, (x - 20) / THRESHOLD)), no = Math.max(0, Math.min(1, (-x - 20) / THRESHOLD));
  const kind = H.kindOf(item.id, cat, card, item.kind);
  const name = (cat && cat.name) || (card && card.name) || '';
  // Google's rating and line, for the card on top only (the dearer tier)
  const [ab, setAb] = useState(() => S.aboutNow(item.id));
  useEffect(() => { let off = false; if (top && card && !card.missing) S.about(item.id).then((v) => { if (!off && v) setAb(v); }); return () => { off = true; }; }, [top, item.id, !!card]);
  const line = (cat && cat.line) || (ab && ab.summary) || (card && card.address) || '';
  return (
    <article className={'co-card' + (top ? ' top' : ' under') + (drag.active ? ' dragging' : '')} aria-label={name} style={style}
      onPointerDown={onDown} onPointerMove={onMove} onPointerUp={onUp} onPointerCancel={() => { start.current = null; setDrag({ x: 0, y: 0, active: false }); }}>
      <Plate card={card} kind={kind} load={top} className="art" by />
      <div className="fade" />
      {item.partnerYes && top ? <span className="badge dv-glass-hero"><span className="dv-avatar is-b">{String(partnerName || '?').charAt(0)}</span>{partnerName} {guessed.has('out:' + item.id) ? 'thinks you’ll say yes' : item.partnerLoved ? 'loved it' : 'said yes'}</span> : null}
      <span className="stamp yes" style={{ opacity: leaving === 'yes' ? 1 : yes }}>Yes</span>
      <span className="stamp no" style={{ opacity: leaving === 'no' ? 1 : no }}>No</span>
      {leaving === 'been' ? <span className="stamp seen" style={{ opacity: 1 }}>Been</span> : null}
      {leaving === 'later' ? <span className="stamp seen" style={{ opacity: 1 }}>Later</span> : null}
      <div className="body">
        <h3 className={'co-display' + (name.length > 20 ? ' long' : '')}>{name}</h3>
        <div className="meta">{ab && ab.rating ? <span className="co-rating"><Ico n="i-star-f" s />{H.ratingWords(ab)}</span> : null}<span className="kind">{H.whatOf(card, cat, words)}</span>{[H.areaOf(card && card.address), card && card.minutes ? card.minutes + ' min' : null].filter(Boolean).join(' · ')}</div>
        {line ? <p className="ov">{line}</p> : null}
        {cat && cat.tags && cat.tags.length ? <div className="tags">{cat.tags.slice(0, 3).map((t) => <span key={t} className="dv-tag">{t}</span>)}</div> : null}
      </div>
    </article>
  );
}

export function SwipeR({ deck, cardsById, catById, loading, error, partnerName, words, canUndo, onDecide, onUndo, onOpen, onFilters, onRetry, narrowed, bar }) {
  const top = deck[0], next = deck[1];
  const tap = (v) => () => { if (window.__coCommit) window.__coCommit(v); };
  if (loading || (top && !cardsById[top.id])) {
    return (
      <div className="co-swipe">
        {bar}
        <div className="co-deck" aria-busy="true" aria-label="Finding places near you"><div className="co-card co-card-sk"><span className="co-plate art"><span className="co-shimmer" /></span><div className="body"><span /><span /><span /></div></div></div>
        <Actions off />
      </div>
    );
  }
  if (error) {
    return (
      <div className="co-swipe">
        {bar}
        <div className="co-out">
          <h2 className="co-display">{error === 'offline' ? 'You’re offline' : 'Couldn’t find places just now'}</h2>
          <p>{error === 'offline' ? 'The deck comes back with a signal.' : 'Google didn’t answer. It usually does on a second go.'}</p>
          {error === 'offline' ? null : <button type="button" className="dv-btn dv-btn-pop" onClick={onRetry}>Try again</button>}
        </div>
      </div>
    );
  }
  if (!top) {
    return (
      <div className="co-swipe">
        {bar}
        <div className="co-out">
          <h2 className="co-display">That’s everywhere for now</h2>
          <p>{narrowed ? 'You’ve narrowed it to ' + narrowed + '. ' : ''}Go further, or try another kind of place.</p>
          <button type="button" className="dv-btn dv-btn-pop" onClick={onFilters}>{narrowed ? 'Show everything' : 'Go further'}</button>
        </div>
        <Actions off canUndo={canUndo} onUndo={onUndo} />
      </div>
    );
  }
  return (
    <div className="co-swipe">
      {bar}
      <div className="co-deck">
        {next && cardsById[next.id] ? <Card key={next.id} item={next} card={cardsById[next.id]} cat={catById[next.id]} top={false} partnerName={partnerName} words={words} onDecide={() => {}} onOpen={() => {}} /> : null}
        <Card key={top.id} item={top} card={cardsById[top.id]} cat={catById[top.id]} top partnerName={partnerName} words={words}
          onDecide={(v) => onDecide(top, v)} onOpen={() => { track('courted_card_opened', {}); onOpen(top.id); }} />
      </div>
      <Actions canUndo={canUndo} onUndo={onUndo} onNo={tap('no')} onYes={tap('yes')} />
      {/* one small line, so they don't read as the labels of ✕ and ♥ (Ben, 1 Nov) */}
      <div className="co-links">
        <button type="button" className="dv-quiet" onClick={tap('later')}>Not sure yet</button>
        <span aria-hidden="true">·</span>
        <button type="button" className="dv-quiet" onClick={tap('been')}>Been there</button>
      </div>
      <Google />
    </div>
  );
}

function Actions({ off, canUndo, onUndo, onNo, onYes }) {
  return (
    <div className="co-acts">
      <button type="button" className="act big" aria-label="Not for us" disabled={off} onClick={onNo}><Ico n="i-x" /></button>
      <button type="button" className="act small" aria-label="Undo" disabled={!canUndo} onClick={onUndo}><Ico n="i-undo" /></button>
      <button type="button" className="act big yes" aria-label="Yes, I’d go" disabled={off} onClick={onYes}><Ico n="i-heart-f" /></button>
    </div>
  );
}

/* Over the deck, one line (Ben, 1 Nov: one row of controls, not three): what, where and when. Tap it for What and
   where. A kind that doesn't fit the time you're swiping for (a hike after dark) says why under it. */
export function WhereBar({ line, note, away, onOpen }) {
  return (
    <div className="co-where">
      <button type="button" className="co-where-btn" aria-haspopup="dialog" onClick={onOpen}>
        <Ico n={away ? 'i-pin' : 'i-house'} s /><span>{line}</span><Ico n="i-chevron-down" s />
      </button>
      {note ? <p className="co-for-note">{note}</p> : null}
    </div>
  );
}

/* What and where: near home (and how far you'll go, for both of you), a town you've looked at, or somewhere else
   (Google's town search: the town is kept for the household, so its yeses stay with it); what kind of date; and
   which free time it's for. Each choice applies as you tap it. */
export function WhatWhereSheet({ Sheet, homeName, range, towns, town, townLines, kinds, left, slots, slot, weekKnown, note,
  onWhere, onRange, onKinds, onWhen, search, onAddTown, onClose }) {
  const [finding, setFinding] = useState(false);
  const [q, setQ] = useState('');
  const [found, setFound] = useState([]);
  const [busy, setBusy] = useState(null);
  const session = useRef(null);
  useEffect(() => {
    if (!finding) return undefined;
    const term = q.trim();
    if (term.length < 2) { setFound([]); return undefined; }
    if (!session.current) session.current = (window.crypto && window.crypto.randomUUID) ? window.crypto.randomUUID() : null;
    let off = false;
    const t = setTimeout(() => { search(term, session.current).then((r) => { if (!off) setFound(r || []); }).catch(() => { if (!off) setFound([]); }); }, 250);
    return () => { off = true; clearTimeout(t); };
  }, [q, finding]);
  const pick = async (r) => {
    setBusy(r.id);
    try { await onAddTown(r, session.current); session.current = null; setFinding(false); setQ(''); setFound([]); }
    catch (e) { /* the app says it couldn't */ }
    setBusy(null);
  };
  const on = (id) => (kinds && kinds.length ? kinds.includes(id) : false);
  const row = (key, ic, tone, title, sub, checked, onClick) => (
    <button key={key} type="button" className="dv-row co-when-row" role="radio" aria-checked={checked ? 'true' : 'false'} onClick={onClick}>
      <span className={'dv-tile' + (tone ? ' ' + tone : '')}><Ico n={ic} s /></span>
      <span className="dv-row-text"><span>{title}</span>{sub ? <small>{sub}</small> : null}</span>
      {checked ? <Ico n="i-check" s /> : null}
    </button>
  );
  const rangeLabel = (H.RANGES.find((r) => r[0] === range) || H.RANGES[1])[1].toLowerCase();
  return (
    <Sheet title="What and where" onClose={onClose} cancel="Done">
      <p className="dv-group-header co-ww-h">Where</p>
      <div className="dv-list dv-list-inset" role="radiogroup" aria-label="Where">
        {row('home', 'i-house', 'is-green', 'Near home', (homeName ? homeName + ', ' : '') + 'within ' + rangeLabel, !town, () => onWhere(null))}
        {(towns || []).map((t) => row(t.id, 'i-pin', '', H.townWords(t), townLines && townLines[t.id], !!town && town.id === t.id, () => onWhere(t)))}
        {!finding ? (
          <button type="button" className="dv-row co-when-row co-ww-else" onClick={() => setFinding(true)}>
            <span className="dv-tile co-ww-find"><Ico n="i-search" s /></span><span className="dv-row-text"><span>Somewhere else</span></span>
          </button>
        ) : null}
      </div>
      {!town ? (
        <div className="co-chipwrap co-ranges co-ww-range" role="radiogroup" aria-label="How far will you go?">
          {H.RANGES.map(([m, l]) => <button key={m} type="button" className="dv-chip" role="radio" aria-checked={range === m ? 'true' : 'false'} aria-pressed={range === m ? 'true' : 'false'} onClick={() => onRange(m)}>{l}</button>)}
        </div>
      ) : null}
      {finding ? (
        <div className="co-ww-search">
          <label className="dv-field dv-field-inset">
            <Ico n="i-search" s />
            <input type="search" autoFocus value={q} onChange={(e) => setQ(e.target.value)} placeholder="A town or city" aria-label="A town or city" enterKeyHint="search" />
          </label>
          {found.length ? (
            <div className="dv-list dv-list-inset">
              {found.map((r) => (
                <button key={r.id} type="button" className="dv-row co-when-row" disabled={!!busy} onClick={() => pick(r)}>
                  <span className="dv-tile"><Ico n="i-pin" s /></span>
                  <span className="dv-row-text"><span>{r.name}</span>{r.detail ? <small>{r.detail}</small> : null}</span>
                  {busy === r.id ? <span className="co-ww-busy" aria-label="Adding" /> : null}
                </button>
              ))}
            </div>
          ) : null}
        </div>
      ) : null}

      <p className="dv-group-header co-ww-h">What</p>
      <div className="co-chipwrap" role="group" aria-label="What kind of date">
        <button type="button" className="dv-chip" aria-pressed={!kinds || !kinds.length ? 'true' : 'false'} onClick={() => onKinds([])}>Everything</button>
        {H.DATE_KINDS.map(([id, l]) => (
          <button key={id} type="button" className="dv-chip" aria-pressed={on(id) ? 'true' : 'false'} aria-disabled={(left || []).includes(id) ? 'true' : undefined}
            onClick={() => onKinds(H.toggleKind(kinds, id))}>{l}</button>
        ))}
      </div>

      <p className="dv-group-header co-ww-h">When</p>
      <div className="co-chipwrap" role="radiogroup" aria-label="When for?">
        <button type="button" className="dv-chip" role="radio" aria-checked={!slot ? 'true' : 'false'} aria-pressed={!slot ? 'true' : 'false'} onClick={() => onWhen(null)}>Any time</button>
        {(slots || []).map((sl) => (
          <button key={sl.date} type="button" className="dv-chip" role="radio" aria-checked={slot && slot.date === sl.date ? 'true' : 'false'} aria-pressed={slot && slot.date === sl.date ? 'true' : 'false'}
            onClick={() => onWhen(sl.date)}>{sl.all ? sl.dow : sl.dow + ' from ' + H.ampm(sl.from)}</button>
        ))}
      </div>
      {!weekKnown ? <p className="dv-note co-note">Your free times show here once Home has worked out the week.</p>
        : !(slots || []).length ? <p className="dv-note co-note">No free time together this week, so far.</p> : null}
      {note ? <p className="dv-note co-note">{note}</p> : null}
      {town ? <p className="dv-note co-note">Each place keeps its own yeses, so {homeName || 'home'}’s don’t come up in {town.name}.</p> : null}
    </Sheet>
  );
}
